import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ConfigColumn {
  title?: string;
  name?: string;
  key?: string;
  type?: string;
  add?: boolean;
  edit?: boolean;
  map?: any;
  regex?: string;
  [key: string]: any;
}

export interface ExtraOption {
  id?: string;
  name?: string;
  [key: string]: any;
}

export interface ModalFormField {
  id: string;
  placeholder: string;
  value: any;
  dropdown: boolean;
  Order_By?: number | string;
  order_by?: number | string;
  orderBy?: number | string;
  apiUrl?: string;
  payload?: any;
  type: string;
  options?: any[];
  optionKey?: string;
  option_mapping?: { id: string; name: string };
  extraOption?: ExtraOption | null;
  matchKey?: string;    // matching key in rowData / options item
  class?: string;
  regex?: string | null;
  regexMessage?: string;
  view?: boolean;       // NEW
  readonly?: boolean;   // NEW
  required?: boolean;
  role?: string[];
  /** Required field is intentionally hidden for this form context. */
  skipRequiredValidation?: boolean;
  conversionType?: string;
  bitCount?: number;
  bitLabels?: string[];
  bitLabelsStr?: string;
  subLabel?: string;
  bits?: any[];
  calculatedTotal?: string;
  minBase?: number | string;
  maxBase?: number | string;
  baseValue?: number | string;
  bitDirection?: 'ltr' | 'rtl';
  minDate?: string | Date;
  maxDate?: string | Date;
  min_date?: string | Date;
  max_date?: string | Date;
  monthYear?: boolean;
  month_year?: boolean;
  hideCheckboxes?: boolean;
  fillFromKey?: string;
  conditionEnabled?: boolean;
  conditionKey?: string;
  conditionOperator?: string;
  conditionValue?: string;
  conditions?: ConditionRule[];
  conditionLogic?: 'AND' | 'OR';
  dynamicProfiles?: any[];
}

export interface ConditionRule {
  key: string;
  operator: string;
  value: string;
  logic?: 'AND' | 'OR';
}

export interface TableConfigClick {
  type: string;
  redirectUrl?: string;
  payload?: { [key: string]: any };
  popup?: { contentUrl?: string; payload?: any };
  apiKey?: string;
  fields?: ModalFormField[];
  rowKeys?: string[];
  confirmAction?: boolean;
  warningMessage?: string;
}

export interface TableConfigColumnMapping {
  mappingUrl?: string;
  mappingKey?: string;
  mappingValue?: string;
  matchKey?: string;
  payload?: any;
  json?: any;
}

export interface TableConfigDropdownOptions {
  url?: string;
  key?: string;
  value?: string;
  matchKey?: string;
  list?: any[];
  payload?: any;
  extraOption?: ExtraOption | null;
  allOption?: ExtraOption | null;
  includeAll?: boolean;
}

export interface TableConfigColumn {
  title: string;
  Order_By?: number | string;
  order_by?: number | string;
  orderBy?: number | string;
  order?: number | string;
  view?: boolean;       // NEW
  readonly?: boolean;   // global/fallback
  readonly_add?: boolean;  // readonly in Add modal only
  readonly_edit?: boolean; // readonly in Edit modal only
  role_readonly?: string[]; // roles for which this field is read-only in both Add and Edit modals
  role_readonly_add?: string[]; // roles for which this field is read-only in Add modal
  role_readonly_edit?: string[]; // roles for which this field is read-only in Edit modal
  required?: boolean;
  show?: boolean;
  display?: boolean;
  type?: string;
  formula?: string;
  expression?: string;
  rowspan?: boolean | string | string[];
  rowSpan?: boolean | string | string[];
  row_formula?: string;
  rowFormula?: string;
  row_expression?: string;
  groupBy?: string | string[];
  group_by?: string | string[];
  valign?: 'top' | 'middle' | 'bottom';
  position?: 'top' | 'middle' | 'bottom';
  halign?: 'left' | 'center' | 'right' | string;
  align?: 'left' | 'center' | 'right' | string;
  textAlign?: 'left' | 'center' | 'right' | string;
  repeatOnEveryPage?: boolean;
  repeat_on_page?: boolean;
  showOnEveryPage?: boolean;
  click?: TableConfigClick;
  mapping?: TableConfigColumnMapping;
  options?: TableConfigDropdownOptions;
  download?: boolean;
  total?: boolean;
  grand_total?: boolean;
  grandTotal?: boolean;
  table_total?: boolean;
  tableTotal?: boolean;
  grid_total?: boolean;
  gridTotal?: boolean;
  pdf_total?: boolean;
  pdfTotal?: boolean;
  csv_total?: boolean;
  csvTotal?: boolean;
  export_total?: boolean;
  exportTotal?: boolean;
  role?: string[];
  role_add?: string[];   // hides field from Add modal only
  role_edit?: string[];  // hides field from Edit modal only
  add?: boolean;
  edit?: boolean;
  regex?: string;
  class?: string;
  regexMessage?: string;
  titleOverrides?: Record<string, string>;
  status?: 'Y' | 'N';
  ignore_value?: boolean;
  ignoreValue?: boolean;
  value?: string;
  defaultValue?: string;
  text?: string;
  staticValue?: string;
  conversionType?: string;
  bitCount?: number;
  bitLabels?: string[];
  bitLabelsStr?: string;
  minDate?: string | Date;
  maxDate?: string | Date;
  min_date?: string | Date;
  max_date?: string | Date;
  monthYear?: boolean;
  month_year?: boolean;
  bitConfig?: {
    conversionType?: string;
    bitCount?: number;
    bitLabels?: string[];
  };
}

export interface TableConfigListMeta {
  title: string;
  dataUrl: string;
  payload?: any;
  dataPayload?: any;
  columns: { [key: string]: TableConfigColumn };
  addUrl?: string;
  addPayload?: any;
  add_payload?: any;
  editUrl?: string;
  editPayload?: any;
  edit_payload?: any;
  edit: boolean;
  add: boolean;
  add_routeurl: string;
  edit_routeurl: string;
  load_after_filter?: boolean;
  loadAfterFilter?: boolean;
  pagination?: boolean;
}

export interface TableConfigEntry {
  _id: string;
  parent_id: string;
  title: string;
  type: string;
  list: TableConfigListMeta;
  filter?: any;
  load_after_filter?: boolean;
  loadAfterFilter?: boolean;
  pagination?: boolean;
  download: {
    csv: boolean;
    pdf: boolean;
  };
}

export interface ModalFormSchema {
  add: ModalFormField[];
  edit: ModalFormField[];
}

@Injectable({
  providedIn: 'root'
})
export class TableSchemaService {
  private dropdownOptionsCache = new Map<string, any[]>();

  constructor(private http: HttpClient) {}

  stripLeadingSlash(url: string): string {
    return url ? url.replace(/^\/+/, '') : url;
  }

  /** Helper to get combined active role identifier (e.g., "FA-GRID" or "FA") */
  private getActiveRoles(): string[] {
    const rawType = (localStorage.getItem('login_Type') || localStorage.getItem('login_type') || '').trim();
    const rawMode = (localStorage.getItem('mode') || '').trim();

    const roles: string[] = [];

    if (rawType) {
      roles.push(rawType.toUpperCase());
      if (rawMode) {
        roles.push(`${rawType.toUpperCase()}-${rawMode.toUpperCase()}`);
      }
    }

    return roles;
  }

  /** Check if any active user roles/modes match a given target array */
  private isRoleBlocked(roleList: string[] | undefined, activeRoles: string[]): boolean {
    if (!roleList || !roleList.length || !activeRoles.length) return false;
    const normalizedRoleList = roleList.map(r => r.toUpperCase());
    return activeRoles.some(activeRole => normalizedRoleList.includes(activeRole));
  }

  /**
   * Converts a Table-Configuration `columns` object into a header array.
   */
buildHeaderFromColumns(columns: { [key: string]: TableConfigColumn }): any[] {
  const activeRoles = this.getActiveRoles();

  return Object.keys(columns || {}).flatMap(key => {
    const col = columns[key] || ({} as TableConfigColumn);
    // An inactive definition must not leak into the grid, form, or export
    // pipelines. Missing status remains backward-compatible and means active.
    if (col.status === 'N') return [];
    const resolvedTitle = this.resolveColumnTitle(col, key, activeRoles);
    const roleBlocked = this.isRoleBlocked(col.role, activeRoles); // NEW

    const formulaStr = col.formula || col.expression || '';
    const ignoreValue = !!((col as any).ignore_value ?? (col as any).ignoreValue);
    const staticValue = (col as any).value ?? (col as any).defaultValue ?? (col as any).text ?? (col as any).staticValue ?? '';
    const header: any = {
      name: resolvedTitle,
      key,
      filter: true,
      show: col.show !== false && !roleBlocked, // NEW: role now affects visibility
      click: !!col.click,
      // `download` is optional in Table-Configuration. A column is included
      // in exports unless the schema explicitly sets `download: false`.
      download: col.download !== false,
      class: col.class || ''
    };

    if (ignoreValue) {
      header.ignore_value = true;
    }

    if (staticValue || ignoreValue) {
      header.value = staticValue;
    }

    if (formulaStr) {
      header.formula = formulaStr;
    } else if (/[+\-*/()]/.test(key) && !key.startsWith('http') && !key.startsWith('/')) {
      header.formula = key;
    }

    const rowspanVal = (col as any).rowspan ?? (col as any).rowSpan;
    if (rowspanVal !== undefined && rowspanVal !== null && rowspanVal !== false) {
      header.rowspan = rowspanVal;
    }
    const rowFormulaStr = (col as any).row_formula ?? (col as any).rowFormula ?? (col as any).row_expression ?? '';
    if (rowFormulaStr) {
      header.row_formula = rowFormulaStr;
    }
    const groupByVal = (col as any).groupBy ?? (col as any).group_by;
    if (groupByVal !== undefined && groupByVal !== null) {
      header.groupBy = groupByVal;
    }
    const valignVal = (col as any).valign ?? (col as any).position;
    if (valignVal) {
      header.valign = valignVal;
      header.position = valignVal;
    }
    const repeatOnEveryPageVal = (col as any).repeatOnEveryPage ?? (col as any).repeat_on_page ?? (col as any).showOnEveryPage;
    if (repeatOnEveryPageVal !== undefined) {
      header.repeatOnEveryPage = repeatOnEveryPageVal;
    }

    if (col.display === false || roleBlocked) {
      header.display = false;
    }

    if (col.click?.type === 'redirect') {
      header.PopUrl = '';
      header.PopRedirect = col.click.redirectUrl || '';
      header.PopPayLoad = col.click.payload || {};
    } else if (col.click?.type === 'popup') {
      header.PopUrl = this.stripLeadingSlash(col.click.popup?.contentUrl || '');
      header.PopRedirect = '';
      header.PopPayLoad = col.click.payload || {};
    } else if (col.click?.type === 'openForm' || col.click?.type === 'form' || col.click?.type === 'action' || col.click?.type === 'confirm') {
      header.PopUrl = '';
      header.PopRedirect = '';
      header.PopPayLoad = col.click.payload || {};
      const rowKeys = Array.isArray(col.click.rowKeys)
        ? col.click.rowKeys
        : (typeof (col.click as any)?.rowKeysStr === 'string'
          ? (col.click as any).rowKeysStr.split(',').map((s: string) => s.trim()).filter(Boolean)
          : []);
      header.openForm = {
        apiKey: col.click.apiKey || '',
        fields: this.normalizeOpenFormFields(col.click.fields || []),
        rowKeys,
        confirmAction: !!(col.click.confirmAction ?? (col.click as any)?.confirm),
        warningMessage: col.click.warningMessage ?? (col.click as any)?.confirmText ?? ''
      };
    }

    if (col.mapping) {
      if (col.mapping.mappingUrl) {
        header.MapByUrl = this.stripLeadingSlash(col.mapping.mappingUrl);
        header.MapByKey = col.mapping.mappingKey || 'id';
        header.MapByValue = col.mapping.mappingValue || 'name';
        header.MapByPayload = col.mapping.payload || {};
        // Also store static json as fallback for values not found in the API (e.g. ALL→All Units)
        if (col.mapping.json && typeof col.mapping.json === 'object' && Object.keys(col.mapping.json).length) {
          header.MapByJsonFallback = col.mapping.json;
        } else if (typeof col.mapping.json === 'string' && col.mapping.json.trim()) {
          try { header.MapByJsonFallback = JSON.parse(col.mapping.json); } catch { /* ignore */ }
        }
      } else if (col.mapping.json) {
        let mapObj = col.mapping.json;
        if (typeof mapObj === 'string') {
          try {
            mapObj = JSON.parse(mapObj);
          } catch {
            mapObj = null;
          }
        }
        if (mapObj && typeof mapObj === 'object') {
          header.MapByUrl = '';
          header.MapByKey = mapObj;
          header.MapByValue = '';
        }
      }
    }

    if (col.role?.length) {
      header.role = col.role; // still sent as-is, unchanged
    }

    return [header];
  });
}
  private resolveColumnTitle(col: TableConfigColumn, key: string, activeRoles: string[]): string {
    if (col.titleOverrides && activeRoles.length) {
      // Check combined mode override first (e.g., "FA-GRID"), fallback to base role (e.g., "FA")
      for (const role of [...activeRoles].reverse()) {
        if (col.titleOverrides[role]) {
          return col.titleOverrides[role];
        }
      }
    }
    return col.title || key;
  }

  mergeConfigColumnsIntoHeader(
    header: any[],
    schemaColumns: { [key: string]: ConfigColumn }
  ): any[] {
    if (!Array.isArray(header) || !schemaColumns) return header;
    const activeRoles = this.getActiveRoles();

    const mergedList = header.map(h => {
      const schemaCol = h?.key ? schemaColumns[h.key] : undefined;
      if (!schemaCol) return h;
      const merged = { ...h, ...schemaCol };
      const formulaStr = schemaCol['formula'] || schemaCol['expression'] || '';
      if (formulaStr) {
        merged.formula = formulaStr;
      } else if (h.key && /[+\-*/()]/.test(h.key) && !h.key.startsWith('http') && !h.key.startsWith('/')) {
        merged.formula = h.key;
      }
      const rowspanVal = schemaCol['rowspan'] ?? schemaCol['rowSpan'];
      if (rowspanVal !== undefined && rowspanVal !== null && rowspanVal !== false) {
        merged.rowspan = rowspanVal;
      }
      const rowFormulaStr = schemaCol['row_formula'] ?? schemaCol['rowFormula'] ?? schemaCol['row_expression'] ?? '';
      if (rowFormulaStr) {
        merged.row_formula = rowFormulaStr;
      }
      const groupByVal = schemaCol['groupBy'] ?? schemaCol['group_by'];
      if (groupByVal !== undefined && groupByVal !== null) {
        merged.groupBy = groupByVal;
      }
      const valignVal = schemaCol['valign'] ?? schemaCol['position'];
      if (valignVal) {
        merged.valign = valignVal;
        merged.position = valignVal;
      }
      const repeatOnEveryPageVal = schemaCol['repeatOnEveryPage'] ?? schemaCol['repeat_on_page'] ?? schemaCol['showOnEveryPage'];
      if (repeatOnEveryPageVal !== undefined) {
        merged.repeatOnEveryPage = repeatOnEveryPageVal;
      }
      const mapping = schemaCol['mapping'];
      if (mapping) {
        if (mapping.mappingUrl) {
          merged.MapByUrl = this.stripLeadingSlash(mapping.mappingUrl);
          merged.MapByKey = mapping.mappingKey || 'id';
          merged.MapByValue = mapping.mappingValue || 'name';
          merged.MapByPayload = mapping.payload || {};
          // Also store static json as fallback for values not found in the API (e.g. ALL→All Units)
          if (mapping.json && typeof mapping.json === 'object' && Object.keys(mapping.json).length) {
            merged.MapByJsonFallback = mapping.json;
          } else if (typeof mapping.json === 'string' && mapping.json.trim()) {
            try { merged.MapByJsonFallback = JSON.parse(mapping.json); } catch { /* ignore */ }
          }
        } else if (mapping.json) {
          let mapObj = mapping.json;
          if (typeof mapObj === 'string') {
            try {
              mapObj = JSON.parse(mapObj);
            } catch {
              mapObj = null;
            }
          }
          if (mapObj && typeof mapObj === 'object') {
            merged.MapByUrl = '';
            merged.MapByKey = mapObj;
            merged.MapByValue = '';
          }
        }
      }
      const click = schemaCol['click'];
      if (click) {
        merged.click = true;
        if (click.type === 'redirect') {
          merged.PopUrl = '';
          merged.PopRedirect = click.redirectUrl || '';
          merged.PopPayLoad = click.payload || {};
        } else if (click.type === 'popup') {
          merged.PopUrl = this.stripLeadingSlash(click.popup?.contentUrl || '');
          merged.PopRedirect = '';
          merged.PopPayLoad = click.payload || {};
        } else if (click.type === 'openForm' || click.type === 'form') {
          merged.PopUrl = '';
          merged.PopRedirect = '';
          merged.PopPayLoad = click.payload || {};
          merged.openForm = {
            apiKey: click.apiKey || '',
            fields: this.normalizeOpenFormFields(click.fields || [])
          };
        }
      }
      return merged;
    });

    const existingKeys = new Set(mergedList.map(h => h?.key));
    for (const key of Object.keys(schemaColumns)) {
      if (!existingKeys.has(key)) {
        const col = schemaColumns[key] as TableConfigColumn;
        if (col && col.status !== 'N' && col.show !== false && col.display !== false) {
          const roleBlocked = this.isRoleBlocked(col.role, activeRoles);
          if (!roleBlocked) {
            const resolvedTitle = this.resolveColumnTitle(col, key, activeRoles);
            const formulaStr = col.formula || col.expression || (/[+\-*/()]/.test(key) && !key.startsWith('http') && !key.startsWith('/') ? key : '');
            const rowspanVal = (col as any).rowspan ?? (col as any).rowSpan;
            const rowFormulaStr = (col as any).row_formula ?? (col as any).rowFormula ?? (col as any).row_expression ?? '';
            const groupByVal = (col as any).groupBy ?? (col as any).group_by;
            const valignVal = (col as any).valign ?? (col as any).position;
            const repeatOnEveryPageVal = (col as any).repeatOnEveryPage ?? (col as any).repeat_on_page ?? (col as any).showOnEveryPage;
            mergedList.push({
              name: resolvedTitle,
              key,
              formula: formulaStr,
              ...(rowFormulaStr ? { row_formula: rowFormulaStr } : {}),
              ...(rowspanVal !== undefined && rowspanVal !== false ? { rowspan: rowspanVal } : {}),
              ...(groupByVal ? { groupBy: groupByVal } : {}),
              ...(valignVal ? { valign: valignVal, position: valignVal } : {}),
              ...(repeatOnEveryPageVal !== undefined ? { repeatOnEveryPage: repeatOnEveryPageVal } : {}),
              filter: true,
              show: true,
              download: col.download !== false,
              class: col.class || ''
            });
          }
        }
      }
    }

    return mergedList;
  }

  normalizeOpenFormFields(fields: any[]): ModalFormField[] {
    const activeRoles = this.getActiveRoles();
    const normalized: ModalFormField[] = (fields || []).map((f: any) => {
      const isDropdownUrl = f.type === 'dropdownUrl' || (f.dropdown && !!f.apiUrl);
      const isDropdownList = f.type === 'dropdownList' || (f.dropdown && !f.apiUrl);
      const isDropdown = isDropdownUrl || isDropdownList || !!f.dropdown;
      const isRoleBlocked = this.isRoleBlocked(f.role, activeRoles);

      const field: ModalFormField = {
        id: f.id || f.key || '',
        placeholder: f.placeholder || f.title || f.name || f.label || '',
        value: f.value ?? '',
        type: f.type || (isDropdown ? (isDropdownUrl ? 'dropdownUrl' : 'dropdownList') : 'text'),
        dropdown: isDropdown,
        class: f.class || 'col-md-6',
        regex: f.regex || null,
        regexMessage: f.regexMessage || '',
        required: f.required === true,
        readonly: f.readonly === true,
        view: isRoleBlocked ? false : f.view !== false,
        skipRequiredValidation: isRoleBlocked && f.required === true,
        role: Array.isArray(f.role) ? f.role : [],
        conversionType: f.conversionType || f.bitConfig?.conversionType || 'decimal',
        bitCount: f.bitCount || f.bitConfig?.bitCount || 12,
        bitLabels: f.bitLabels || f.bitConfig?.bitLabels || (f.bitLabelsStr ? f.bitLabelsStr.split(',').map((s: string) => s.trim()).filter(Boolean) : []),
        bitLabelsStr: f.bitLabelsStr || f.subLabel || f.sub_label || '',
        subLabel: f.subLabel || f.sub_label || f.bitLabelsStr || '',
        minBase: f.minBase !== undefined ? f.minBase : (f.bitConfig?.minBase ?? ''),
        maxBase: f.maxBase !== undefined ? f.maxBase : (f.bitConfig?.maxBase ?? ''),
        baseValue: f.baseValue !== undefined ? f.baseValue : (f.bitConfig?.baseValue ?? ''),
        bitDirection: f.bitDirection || f.bitConfig?.bitDirection || 'ltr',
        hideCheckboxes: f.hideCheckboxes === true,
        fillFromKey: f.fillFromKey || f.sourceKey || f.rowKey || '',
        conditionEnabled: f.conditionEnabled === true,
        conditionKey: f.conditionKey || '',
        conditionOperator: f.conditionOperator || '==',
        conditionValue: f.conditionValue !== undefined ? String(f.conditionValue) : '',
        conditions: Array.isArray(f.conditions) && f.conditions.length
          ? f.conditions.map((c: any) => ({
              key: c.key || '',
              operator: c.operator || '==',
              value: c.value !== undefined ? String(c.value) : '',
              logic: c.logic || 'AND'
            }))
          : (f.conditionKey ? [{ key: f.conditionKey, operator: f.conditionOperator || '==', value: f.conditionValue !== undefined ? String(f.conditionValue) : '', logic: 'AND' }] : []),
        conditionLogic: f.conditionLogic || 'AND',
        Order_By: f.Order_By ?? f.order_by ?? f.orderBy,
        minDate: f.minDate ?? f.min_date,
        maxDate: f.maxDate ?? f.max_date,
        monthYear: f.monthYear === true || f.month_year === true
      };

      if (isDropdownUrl) {
        field.apiUrl = this.stripLeadingSlash(f.apiUrl || f.url || '');
        field.optionKey = f.optionKey || f.option_mapping?.id || 'id';
        field.option_mapping = {
          id: f.option_mapping?.id || f.optionKey || 'id',
          name: f.option_mapping?.name || 'name'
        };
        field.extraOption = f.extraOption || f.allOption || null;
        field.options = Array.isArray(f.options) ? f.options : [];
      } else if (isDropdownList) {
        field.options = Array.isArray(f.options)
          ? f.options.map((o: any) => ({
              id: o.id !== undefined ? o.id : (o.value !== undefined ? o.value : ''),
              name: o.name !== undefined ? o.name : (o.label !== undefined ? o.label : '')
            }))
          : [];
        field.optionKey = f.optionKey || 'id';
        field.option_mapping = { id: 'id', name: 'name' };
      }

      return field;
    });

    const hasCustomOrder = normalized.some((f: ModalFormField) => {
      const raw = f.Order_By ?? (f as any).order_by ?? (f as any).orderBy;
      return raw !== undefined && raw !== null && raw !== '' && !isNaN(Number(raw));
    });

    if (hasCustomOrder) {
      normalized.sort((a: ModalFormField, b: ModalFormField) => {
        const rawA = a.Order_By ?? (a as any).order_by ?? (a as any).orderBy;
        const rawB = b.Order_By ?? (b as any).order_by ?? (b as any).orderBy;
        const numA = (rawA !== undefined && rawA !== null && rawA !== '' && !isNaN(Number(rawA))) ? Number(rawA) : Number.MAX_SAFE_INTEGER;
        const numB = (rawB !== undefined && rawB !== null && rawB !== '' && !isNaN(Number(rawB))) ? Number(rawB) : Number.MAX_SAFE_INTEGER;
        return numA - numB;
      });
    }

    return normalized;
  }

  async buildModalFieldsFromTableConfigColumns(
    columns: { [key: string]: TableConfigColumn },
    siteId?: string | number
  ): Promise<ModalFormSchema> {
    const add: ModalFormField[] = [];
    const edit: ModalFormField[] = [];
    const built: { col: TableConfigColumn; field: ModalFormField }[] = [];

    Object.keys(columns || {}).forEach(key => {
      const col = columns[key];
      if (!col || col.status === 'N') return;

      const isDropdownUrl = col.type === 'dropdownUrl';
      const isStaticMap = col.type === 'dropdownList';
      const extraOption = col.options?.extraOption || col.options?.allOption || (col as any).extraOption || null;

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayDateStr = `${year}-${month}-${day}`;

      const rawOrder = col.Order_By ?? (col as any).order_by ?? (col as any).orderBy ?? (col as any).order;
      const field: ModalFormField = {
        id: key,
        placeholder: col.title || key,
        value: (col.type === 'date' && col.readonly) ? todayDateStr : '',
        dropdown: isDropdownUrl || isStaticMap,
        class: col.class || 'col',
        regex: col.regex || null,
        regexMessage: col.regexMessage || '',
        type: col.type || 'text',
        Order_By: (rawOrder !== undefined && rawOrder !== null && rawOrder !== '' && !isNaN(Number(rawOrder))) ? Number(rawOrder) : rawOrder,
        conversionType: col.conversionType || col.bitConfig?.conversionType || 'decimal',
        bitCount: col.bitCount || col.bitConfig?.bitCount || 12,
        bitLabels: col.bitLabels || col.bitConfig?.bitLabels || [],
        view: col.view,           // NEW
        readonly: col.readonly,   // NEW
        matchKey: col.options?.matchKey || col.mapping?.matchKey || (col as any).matchKey,
        required: col.required === true,
        minDate: col.minDate ?? (col as any).min_date,
        maxDate: col.maxDate ?? (col as any).max_date,
        monthYear: col.monthYear === true || (col as any).month_year === true,
        extraOption: extraOption ? { id: extraOption.id ?? 'ALL', name: extraOption.name ?? 'All Units' } : null
      };

      const inlineList = Array.isArray(col.options?.list) ? col.options!.list : null;

      if (inlineList && inlineList.length) {
        const optionKey = col.options?.key || 'id';
        const optionValue = col.options?.value || 'name';
        field.options = inlineList.map((item: any) => ({
          id: item?.[optionKey] ?? item?.id,
          name: item?.[optionValue] ?? item?.name ?? item?.[optionKey] ?? item?.id,
          ...item
        }));
        field.optionKey = optionKey;
        field.option_mapping = { id: optionKey, name: optionValue };
      } else if (isDropdownUrl) {
        const url = this.stripLeadingSlash(col.options?.url || col.mapping?.mappingUrl || '');
        const optionKey = col.options?.key || col.mapping?.mappingKey || 'id';
        const optionValue = col.options?.value || col.mapping?.mappingValue || 'name';
        field.apiUrl = url;
        field.optionKey = optionKey;
        field.option_mapping = { id: optionKey, name: optionValue };
        field.options = [];
        if (extraOption) {
          field.extraOption = { id: extraOption.id ?? 'ALL', name: extraOption.name ?? 'All Units' };
        }
      } else if (isStaticMap && col.mapping?.json && typeof col.mapping.json === 'object') {
        field.options = Object.entries(col.mapping.json).map(([optKey, optValue]) => ({
          id: optKey,
          name: optValue as string
        }));
      }

      built.push({ col, field });
    });

    await Promise.all(
      built
        .filter(b => b.field.type === 'dropdownUrl' && b.field.apiUrl && !b.field.options?.length)
        .map(async b => {
          b.field.options = await this.fetchDropdownOptions(
            b.field.apiUrl!,
            b.field.optionKey || 'id',
            b.field.option_mapping?.name || 'name',
            siteId,
            b.col.options?.payload || b.col.mapping?.payload,
            b.field.extraOption || b.col.options?.extraOption
          );
        })
    );

    // Pre-fetch dropdown options for any openForm fields defined on columns with click.type === 'openForm'
    const openFormDropdowns: ModalFormField[] = [];
    Object.keys(columns || {}).forEach(key => {
      const col = columns[key];
      if ((col?.click?.type === 'openForm' || col?.click?.type === 'form') && Array.isArray(col.click.fields)) {
        col.click.fields.forEach((f: any) => {
          if ((f.type === 'dropdownUrl' || (f.dropdown && f.apiUrl)) && f.apiUrl && (!f.options || !f.options.length)) {
            openFormDropdowns.push(f);
          }
        });
      }
    });

    if (openFormDropdowns.length) {
      await Promise.all(
        openFormDropdowns.map(async f => {
          const url = this.stripLeadingSlash(f.apiUrl || '');
          const optKey = f.optionKey || f.option_mapping?.id || 'id';
          const optVal = f.option_mapping?.name || 'name';
          f.options = await this.fetchDropdownOptions(url, optKey, optVal, siteId, f.payload, f.extraOption);
        })
      );
    }


const activeRoles = this.getActiveRoles();

built.forEach(({ col, field }) => {
  const blockedBoth = this.isRoleBlocked(col.role, activeRoles);
  const blockedAdd = blockedBoth || this.isRoleBlocked(col.role_add, activeRoles);
  const blockedEdit = blockedBoth || this.isRoleBlocked(col.role_edit, activeRoles);

  const isReadonlyBoth = this.isRoleBlocked(col.role_readonly, activeRoles);
  const isReadonlyAdd = isReadonlyBoth || this.isRoleBlocked(col.role_readonly_add, activeRoles);
  const isReadonlyEdit = isReadonlyBoth || this.isRoleBlocked(col.role_readonly_edit, activeRoles);

  // Explicit rule: if the column is hidden from the grid (display: false) and
  // also marked non-viewable (view: false), but it's still editable (add/edit),
  // force view: false onto the built field so the form always receives it
  // explicitly rather than relying on it being carried over during field creation.
  const effectiveDisplayFalse = col.display === false || blockedBoth;

  const buildFormField = (roleBlockedForForm: boolean, mode: 'add' | 'edit'): ModalFormField => {
    const formField = { ...field };
    if (mode === 'add') {
      formField.readonly = isReadonlyAdd || (col.readonly_add !== undefined ? col.readonly_add : (col.readonly === true));
    } else if (mode === 'edit') {
      formField.readonly = isReadonlyEdit || (col.readonly_edit !== undefined ? col.readonly_edit : (col.readonly === true));
    }
    // A required field hidden by grid visibility, form visibility, or a role
    // rule must still be included in the payload. It cannot be user-entered,
    // so it must not fail client-side required validation.
    const hiddenForForm = col.view === false || effectiveDisplayFalse || roleBlockedForForm;
    if (hiddenForForm) {
      formField.view = false;
      formField.skipRequiredValidation = col.required === true;
    }
    return formField;
  };

  // Role-blocked optional fields remain excluded, as before. Required fields
  // remain in the action payload even when the role hides them.
  if (col.add && (!blockedAdd || col.required === true)) add.push(buildFormField(blockedAdd, 'add'));
  if (col.edit && (!blockedEdit || col.required === true)) edit.push(buildFormField(blockedEdit, 'edit'));
});

    const sortByOrderBy = (fields: ModalFormField[]): ModalFormField[] => {
      const hasCustomOrder = fields.some(f => {
        const raw = f.Order_By ?? (f as any).order_by ?? (f as any).orderBy;
        return raw !== undefined && raw !== null && raw !== '' && !isNaN(Number(raw));
      });

      if (!hasCustomOrder) {
        return fields;
      }

      return [...fields].sort((a, b) => {
        const rawA = a.Order_By ?? (a as any).order_by ?? (a as any).orderBy;
        const rawB = b.Order_By ?? (b as any).order_by ?? (b as any).orderBy;

        const numA = (rawA !== undefined && rawA !== null && rawA !== '' && !isNaN(Number(rawA)))
          ? Number(rawA)
          : Number.MAX_SAFE_INTEGER;

        const numB = (rawB !== undefined && rawB !== null && rawB !== '' && !isNaN(Number(rawB)))
          ? Number(rawB)
          : Number.MAX_SAFE_INTEGER;

        return numA - numB;
      });
    };

    return {
      add: sortByOrderBy(add),
      edit: sortByOrderBy(edit)
    };
  }

  private async fetchDropdownOptions(
    url: string,
    key: string,
    value: string,
    siteId?: string | number,
    extraPayload?: any,
    extraOption?: ExtraOption | null
  ): Promise<any[]> {
    const payloadKeyStr = extraPayload ? JSON.stringify(extraPayload) : '';
    const extraOptStr = extraOption ? JSON.stringify(extraOption) : '';
    const cacheKey = `${url}_${key}_${value}_${siteId ?? ''}_${payloadKeyStr}_${extraOptStr}`;
    if (this.dropdownOptionsCache.has(cacheKey)) {
      return this.dropdownOptionsCache.get(cacheKey)!;
    }

    let params: any = { site_id: siteId ?? '', Limit: 10000, Offset: 0 };
    if (extraPayload && typeof extraPayload === 'object') {
      params = { ...params, ...extraPayload };
    }

    const res: any = await firstValueFrom(
      this.http
        .get<any>(`${environment.apiURL}/${url}`, {
          params
        })
        .pipe(
          timeout(8000),
          catchError(() => of(null))
        )
    );

    const list = res?.Data?.list ?? res?.Data ?? res ?? [];
    let options = (Array.isArray(list) ? list : []).map((item: any) => ({
      id: item?.[key] ?? item?.id,
      name: item?.[value] ?? item?.name ?? item?.[key] ?? item?.id,
      ...item
    }));

    if (extraOption && (extraOption.id !== undefined || extraOption.name !== undefined)) {
      options = [
        { id: extraOption.id ?? 'ALL', name: extraOption.name ?? 'All Units' },
        ...options
      ];
    }

    this.dropdownOptionsCache.set(cacheKey, options);
    return options;
  }

  clearDropdownOptionsCache(): void {
    this.dropdownOptionsCache.clear();
  }
}
